import { getSchoolYear } from "../../mp-utils/generic-utils"
import { format, subDays, startOfDay, parseISO } from "date-fns"
import { DATE_FORMAT, DATE_OPTIONS, DATE_ALIAS } from "../constants"
import { PeriodValue } from "../types"
import { DateRangeShortcutsItemsType } from "../components/DateRangeCalendar/types"
import { getUseSchoolYear } from "./flags"

export const getRangeValue = (period: PeriodValue | Date[]): string[] => {
  const now = new Date()

  if (Array.isArray(period)) {
    return [format(period[0], DATE_FORMAT), format(period[1], DATE_FORMAT)]
  }

  switch (period) {
    case "custom": {
      return [format(subDays(now, 1), DATE_FORMAT), format(now, DATE_FORMAT)]
    }
    case "last 7 days": {
      return [format(subDays(now, 6), DATE_FORMAT), format(now, DATE_FORMAT)]
    }
    case "last 30 days": {
      return [format(subDays(now, 29), DATE_FORMAT), format(now, DATE_FORMAT)]
    }
    case "this school year": {
      const startYear = getSchoolYear().year
      return [
        format(new Date(startYear, 7, 1), DATE_FORMAT),
        format(now, DATE_FORMAT)
      ]
    }
    case "entire school year": {
      const startYear = getSchoolYear().year
      return [
        format(new Date(startYear, 7, 1), DATE_FORMAT),
        format(new Date(startYear + 1, 6, 31), DATE_FORMAT)
      ]
    }
    case "last school year": {
      const startYear = getSchoolYear().year - 1
      return [
        format(new Date(startYear, 7, 1), DATE_FORMAT),
        format(new Date(startYear + 1, 6, 31), DATE_FORMAT)
      ]
    }
    default: {
      return []
    }
  }
}

export const getDateRangeShortcutsItems = (): DateRangeShortcutsItemsType[] => {
  return DATE_OPTIONS.map(({ value, label }) => ({
    label,
    getValue: () => {
      const [minDate, maxDate] = getRangeValue(value)

      return [parseISO(minDate), parseISO(maxDate)]
    }
  }))
}

export const getPeriodValue = (value: string[]): PeriodValue => {
  const [start, end] = value || []

  const now = new Date()

  if (start === format(subDays(now, 6), DATE_FORMAT)) {
    return "last 7 days"
  }

  if (start === format(subDays(now, 29), DATE_FORMAT)) {
    return "last 30 days"
  }

  const startYear = getSchoolYear().year

  // This school year and entire school year
  const currentSchoolYearStart = format(new Date(startYear, 7, 1), DATE_FORMAT)
  const currentSchoolYearEnd = format(
    new Date(startYear + 1, 6, 31),
    DATE_FORMAT
  )

  // timestamps
  const currentSchoolYearEndTimestamp = startOfDay(
    new Date(startYear + 1, 6, 31)
  ).getTime()
  const currentSchoolYearNowTimestamp = startOfDay(now).getTime()
  const endPeriodTimestamp = parseISO(end).getTime()

  // Check for entire school year first (exact match)
  if (start === currentSchoolYearStart && end === currentSchoolYearEnd) {
    return "entire school year"
  }

  // Check for current school year (up to today)
  if (
    start === currentSchoolYearStart &&
    endPeriodTimestamp >= currentSchoolYearNowTimestamp &&
    endPeriodTimestamp <= currentSchoolYearEndTimestamp
  ) {
    return "this school year"
  }

  // Last school year
  const lastSchoolYearStart = format(new Date(startYear - 1, 7, 1), DATE_FORMAT)
  const lastSchoolYearEnd = format(new Date(startYear, 6, 31), DATE_FORMAT)

  if (start === lastSchoolYearStart && end === lastSchoolYearEnd) {
    return "last school year"
  }

  return "custom"
}

export const getPeriodLabel = (
  range: string[],
  isChipLabel: boolean = true
): string => {
  const periodValue = (() => {
    const value = getPeriodValue(range)
    if( DATE_ALIAS[value] ) {
      return DATE_ALIAS[value]
    }
    return value
  })()
  const option = DATE_OPTIONS.find(
    option => option.value === periodValue
  )

  if( !getUseSchoolYear() && range?.length === 0 ) return ''
  if (!option) return "Custom"

  return !!isChipLabel ? option.chipLabel || option.label : option.label
}
